import styled, { css, keyframes } from 'styled-components';

/* ---------- Identity bar ---------- */

export const IdentityBar = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 1rem 0.4rem 0.4rem;
  border-radius: 999rem;
  background: ${({ theme }) => theme.colors.surfaceSoft};
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.slate};
  margin-bottom: 0.8rem;
`;

export const IdentityAvatar = styled.div<{ $accent: string }>`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  background: ${({ $accent }) => `${$accent}1F`};
  color: ${({ $accent }) => $accent};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  font-weight: 800;
`;

export const IdentityEdit = styled.button<{ $accent: string }>`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 700;
  font-size: 1.2rem;
  color: ${({ $accent }) => $accent};
  cursor: pointer;
  margin-left: 0.4rem;
  &:hover {
    text-decoration: underline;
  }
`;

export const IdentityInputRow = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  margin-bottom: 0.8rem;
`;

export const IdentityInput = styled.input`
  flex: 1;
  padding: 0.8rem 1rem;
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 0.8rem;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.darkSlate};
  background: ${({ theme }) => theme.colors.white};
  &:focus-visible {
    outline: 0.2rem solid ${({ theme }) => theme.colors.accent};
    outline-offset: 0.1rem;
    border-color: transparent;
  }
`;

export const IdentitySaveBtn = styled.button<{ $accent: string }>`
  border: none;
  padding: 0.8rem 1.2rem;
  border-radius: 0.8rem;
  background: ${({ $accent }) => $accent};
  color: ${({ theme }) => theme.colors.white};
  font-weight: 700;
  font-size: 1.3rem;
  cursor: pointer;
`;

/* ---------- Provider header ---------- */

export const ProviderName = styled.div`
  font-size: 2.4rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.darkSlate};
  letter-spacing: -0.02rem;
  line-height: 1.15;
`;

export const ProviderMeta = styled.div`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin-top: 0.4rem;
  margin-bottom: 2.4rem;
  text-transform: capitalize;
`;

/* ---------- Sections ---------- */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(0.6rem); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const Section = styled.section<{ $reducedMotion: boolean }>`
  margin-top: 2.8rem;
  ${({ $reducedMotion }) =>
    !$reducedMotion &&
    css`
      animation: ${fadeIn} 0.28s ease-out both;
    `}
`;

export const SectionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkSlate};
  margin: 0 0 1.6rem;
`;

export const SectionHint = styled.p`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin: 0 0 1.6rem;
  line-height: 1.5;
`;

/* ---------- Rating row ---------- */

export const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  padding: 0.6rem 0;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
  }
`;

export const CategoryLabel = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.slate};
  text-transform: capitalize;
`;

/* ---------- Overall summary ---------- */

export const OverallSummary = styled.div<{
  $accent: string;
  $hasRating: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.2rem;
  margin-top: 1.6rem;
  padding: 1rem 1.4rem;
  border-radius: 1rem;
  background: ${({ $accent, $hasRating }) =>
    $hasRating ? `${$accent}14` : 'transparent'};
  border: 0.1rem solid
    ${({ $accent, $hasRating, theme }) =>
      $hasRating ? `${$accent}40` : theme.colors.border};
  transition:
    background 0.2s ease,
    border-color 0.2s ease;
`;

export const OverallLabel = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-transform: uppercase;
  letter-spacing: 0.05rem;
`;

export const OverallStars = styled.div<{ $accent: string }>`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.amber};
  font-feature-settings: 'tnum';
`;

export const OverallNumber = styled.span`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.darkSlate};
`;

/* ---------- Group review row ---------- */

export const GroupRow = styled.div`
  padding: 1.2rem 0;
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.border};
  &:last-child {
    border-bottom: none;
  }
`;

export const GroupName = styled.div`
  font-weight: 700;
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.darkSlate};
  margin-bottom: 0.4rem;
`;

export const GroupRole = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.slateGrey};
`;

export const GroupTextarea = styled.textarea`
  width: 100%;
  margin-top: 0.6rem;
  padding: 0.8rem 1rem;
  font-size: 1.3rem;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.darkSlate};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 0.8rem;
  resize: vertical;
  &:focus-visible {
    outline: 0.2rem solid ${({ theme }) => theme.colors.accent};
    outline-offset: 0.1rem;
    border-color: transparent;
  }
`;

/* ---------- Soft toggles ---------- */

export const SoftToggle = styled.label`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 1rem 1.2rem;
  border-radius: 1rem;
  background: ${({ theme }) => theme.colors.surfaceSoft};
  cursor: pointer;
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.slate};
  line-height: 1.5;
  margin-top: 1.2rem;

  input[type='checkbox'],
  input[type='radio'] {
    margin-top: 0.2rem;
    accent-color: ${({ theme }) => theme.colors.accent};
    width: 1.6rem;
    height: 1.6rem;
  }
`;

export const ImprovementVisibility = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 1.2rem;
`;

export const ImprovementHint = styled.div`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  margin-top: 0.4rem;
  line-height: 1.5;
`;

/* ---------- Field group (textarea labels) ---------- */

export const FieldGroup = styled.div`
  margin-top: 1.6rem;
  &:first-child {
    margin-top: 0;
  }
`;

export const FieldLabel = styled.label`
  display: block;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.darkSlate};
  margin-bottom: 0.6rem;
`;

export const Textarea = styled.textarea`
  width: 100%;
  min-height: 9rem;
  padding: 1rem 1.2rem;
  font-size: 1.4rem;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.darkSlate};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1rem;
  background: ${({ theme }) => theme.colors.white};
  line-height: 1.5;
  resize: vertical;
  transition: border-color 0.15s ease;
  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
  &:focus-visible {
    outline: 0.2rem solid ${({ theme }) => theme.colors.accent};
    outline-offset: 0.1rem;
    border-color: transparent;
  }
`;

/* ---------- Select (timeframe) ---------- */

export const Select = styled.select`
  width: 100%;
  padding: 1rem 1.2rem;
  font-size: 1.4rem;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.darkSlate};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 1rem;
  background: ${({ theme }) => theme.colors.white};
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' width='12' height='8'%3E%3Cpath fill='%236B7280' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1.2rem center;
  padding-right: 3rem;
  &:focus-visible {
    outline: 0.2rem solid ${({ theme }) => theme.colors.accent};
    outline-offset: 0.1rem;
    border-color: transparent;
  }
`;

/* ---------- Media ---------- */

export const ThumbsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-bottom: 1rem;
`;

export const Thumb = styled.div`
  position: relative;
  width: 8rem;
  height: 8rem;
  border-radius: 0.8rem;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const PlayBadge = styled.div`
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 1rem;
  padding: 0.2rem 0.4rem;
  border-radius: 0.4rem;
`;

export const RemoveBtn = styled.button`
  position: absolute;
  top: -0.4rem;
  right: -0.4rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.slate};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AudioThumb = styled.div`
  position: relative;
  width: 18rem;
  height: 6.4rem;
  border-radius: 0.8rem;
  background: ${({ theme }) => theme.colors.surfaceSoft};
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0 0.8rem;
`;

export const AudioIcon = styled.span`
  font-size: 1.6rem;
`;

export const AudioPlayer = styled.audio`
  flex: 1;
  height: 3.2rem;
`;

export const CaptureRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

export const CaptureBtn = styled.button`
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.slate};
  font-size: 1.3rem;
  font-weight: 600;
  padding: 0.8rem 1.2rem;
  border-radius: 0.8rem;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.surfaceSoft};
  }
  &:focus-visible {
    outline: 0.2rem solid ${({ theme }) => theme.colors.accent};
    outline-offset: 0.1rem;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const UploadingHint = styled.span`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  align-self: center;
`;

export const UploadError = styled.div`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.errorDark};
  background: ${({ theme }) => theme.colors.errorBg};
  padding: 0.6rem 1rem;
  border-radius: 0.8rem;
  margin-top: 0.8rem;
`;

/* ---------- Submit ---------- */

export const SubmitButton = styled.button<{ $accent: string }>`
  width: 100%;
  margin-top: 2.8rem;
  padding: 1.4rem;
  border: none;
  border-radius: 1.2rem;
  background: ${({ $accent }) => $accent};
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.5rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    transform 0.1s ease,
    opacity 0.15s ease;
  &:hover:not(:disabled) {
    transform: translateY(-0.1rem);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 0.2rem solid ${({ theme }) => theme.colors.accent};
    outline-offset: 0.2rem;
  }
`;

export const SubmitHint = styled.div`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.slateGrey};
  text-align: center;
  margin-top: 0.8rem;
`;
